import { createGlobalStyle, css } from "styled-components";

import resetCSS from "./reset";

import {
  colors,
  getBasePadding,
  getPagePadding,
  maxWidth,
} from "@/common/theme";

import { setScalableFontSize } from "../responsive";
import { easeOutQuad, outQuad } from "./easing";
import { setTypography } from "./mixins";
import { getNavigationHeight } from "@/components/Navigation/NavigationBar";

const themeTransitionDuration = 600;
export const themeTransition = {
  duration: themeTransitionDuration,
  durationCSS: themeTransitionDuration / 1000 + "s",
  ease: easeOutQuad,
  easeFunction: outQuad,
  easeGSAP: "quad.out",
};

const custom = css`
  html {
    min-height: 100%;
    height: auto;
    background-color: ${({ theme }) => theme.theme.background};
    color: ${colors.white};
    overscroll-behavior-x: none;
  }

  html {
    overflow: hidden;
  }

  body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background-color: ${({ theme }) => theme.theme.background};
  }

  body {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.theme.background};
    color: ${colors.white};
  }

  #__next {
    display: flex;
    flex-direction: column;
    flex: 1 0 auto;
    width: 100%;

    height: 100%;
    overflow-y: hidden;
    overflow-x: hidden;
    position: relative;
    scrollbar-width: none;
    -ms-overflow-style: none;
    overscroll-behavior: none;
  }

  :root {
    --navigationHeight: ${({ theme }) =>
      getNavigationHeight(theme.isMobileLayout)};
    --basePadding: ${({ theme }) => getBasePadding(theme.isMobileLayout)};
    --pagePadding: max(
      var(--basePadding),
      calc((100vw - ${maxWidth}px) / 2 + var(--basePadding))
    );
    --theme-transition-duration: ${themeTransition.durationCSS};
    --theme-transition-ease: ${themeTransition.ease};
  }
`;

const lenis = css`
  html.lenis {
    /* height: auto; */
  }

  .lenis.lenis-smooth {
    scroll-behavior: auto;
  }

  .lenis.lenis-smooth [data-lenis-prevent] {
    overscroll-behavior: contain;
  }

  .lenis.lenis-stopped {
    overflow: hidden;
  }

  .lenis.lenis-scrolling iframe {
    pointer-events: none;
  }
`;

const style = css`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    outline: none;
    user-select: text;

    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  html.theme-transitioning * {
    transition: color var(--theme-transition-duration)
      var(--theme-transition-ease);
    will-change: color;
  }

  .performance-stats__value,
  .performance-stats__label {
    color: #fff;
  }

  html.theme-transitioning,
  html.theme-transitioning div,
  html.theme-transitioning article,
  html.theme-transitioning nav,
  html.theme-transitioning section,
  html.theme-transitioning header,
  html.theme-transitioning body,
  html.theme-transitioning main,
  html.theme-transitioning hr {
    transition:
      border-color var(--theme-transition-duration) var(--theme-transition-ease),
      background-color var(--theme-transition-duration)
        var(--theme-transition-ease);
    will-change: background-color;
  }

  html.theme-transitioning path {
    transition:
      fill var(--theme-transition-duration) var(--theme-transition-ease),
      stroke var(--theme-transition-duration) var(--theme-transition-ease);
    will-change: fill, stroke;
  }

  a {
    display: inline-block;
  }

  ::selection {
    background: ${colors.black};
    color: ${colors.white};
    text-shadow: none;
  }

  ::-moz-selection {
    background: ${colors.black};
    color: ${colors.white};
    text-shadow: none;
  }

  html {
    ${setScalableFontSize()}
    position: relative;
    overflow-x: hidden;
  }

  body {
    overflow-x: hidden;
  }

  button,
  input,
  textarea,
  select,
  dialog {
    -webkit-appearance: none;
    background: none;
    font-family: inherit;
    border: none;
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
    text-transform: inherit;
    outline: none;
    border-radius: 0;
  }

  svg {
    outline: none;
    display: block;
  }

  button,
  div,
  a,
  span {
    -webkit-tap-highlight-color: transparent;
  }

  a {
    color: inherit;
  }

  button {
    cursor: pointer;
  }

  ${custom}
  ${lenis}

  h1 {
    ${({ theme: { locale } }) => setTypography("heading1", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }
  h2 {
    ${({ theme: { locale } }) => setTypography("heading2", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }
  h3 {
    ${({ theme: { locale } }) => setTypography("heading3", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }
  h4 {
    ${({ theme: { locale } }) => setTypography("heading4", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }
  h5 {
    ${({ theme: { locale } }) => setTypography("heading5", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }
  h6 {
    ${({ theme: { locale } }) => setTypography("heading6", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }
  p {
    ${({ theme: { locale } }) => setTypography("paragraph", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }

  strong {
    ${({ theme: { locale } }) => setTypography("paragraphStrong", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }

  .txt-shuffle,
  .txt-shuffle-wrapper {
    ${({ theme: { locale } }) => setTypography("monoSmall", locale)};
    color: ${({ theme }) => theme.theme.textColor};
  }

  img {
    object-fit: cover !important;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  span {
    text-wrap: pretty;
  }
`;

export default createGlobalStyle`
    ${resetCSS}
    ${style}

    .splittext-line-parent {
        clip-path: polygon(0 0, 100% 0, 100% 110%, 0% 100%);
    }

    .splittext-parent {
      position: relative;
      overflow: hidden;
    }

    .splittext-original {
      position: absolute;
      top: 0;
      left: 0;
    }


    .splittext-child {
      margin-bottom: -0.5rem;
      padding-bottom: 0.5rem;
    }

    .splittext-child-mask,
    .splittext-char-mask {
      overflow: visible !important;
      clip-path: inset(-0.1em -0.1em -0.25em -0.1em);
    }


    /* Ensure main content area takes up full height */
    main {
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;
    }

    html {
      min-height: 100%;
      height: auto !important;
    }
`;
